import type { LocateSkill } from '@sb/ai/types';
import { convertEulerPose } from '@sb/geometry';
import type { Step } from '@sb/remote-control/types';
import type { Space } from '@sb/routine-runner';
import { six } from '@sb/utilities';

export const VIRTUAL_CAPTURE_POSE_ID_PREFIX = 'virtual-capture-pose:';

export const capturePoseSpaceItemId = (stepId: string): string =>
  `${VIRTUAL_CAPTURE_POSE_ID_PREFIX}${stepId}`;

export function getVirtualSpaceItems(
  stepConfigurations: Record<string, Step.ConvertedConfiguration>,
  getLocateSkillById?: (id: string) => LocateSkill | undefined,
): Space.Item[] {
  const space: Space.Item[] = [];

  for (const step of Object.values(stepConfigurations)) {
    if (step.stepKind === 'Locate' && step.args) {
      space.push({
        kind: 'visionPositionList',
        id: step.id,
        global: false,
        name: step.args.positionListName,
        description: '',
        positions: [],
        planeID: step.args.planeID,
      });
    }

    if (step.stepKind === 'RunLocateSkill' && step.args) {
      // Resolve the skill up front so both derived items can fall back to
      // skill-name-based default names.
      let skill: LocateSkill | undefined;

      try {
        skill =
          getLocateSkillById && step.args.skillId
            ? getLocateSkillById(step.args.skillId)
            : undefined;
      } catch {
        // Defensive: a malformed resolver lookup should not throw the
        // whole conversion.
        skill = undefined;
      }

      space.push({
        kind: 'visionPositionList',
        id: step.id,
        global: false,
        name:
          step.args.positionListName ??
          (skill?.name ? `${skill.name} locate results` : 'Locate results'),
        description: '',
        positions: [],
        // Plane is resolved from the skill at runtime; no config-time planeID.
        planeID: undefined,
      });

      if (skill && skill.shared.capturePose !== null) {
        try {
          const p = skill.shared.capturePose;

          const pose = convertEulerPose.toCartesian({
            X: p.x,
            Y: p.y,
            Z: p.z,
            Rx: p.roll,
            Ry: p.pitch,
            Rz: p.yaw,
          });

          const name =
            step.args.capturePoseName ??
            (skill.name ? `${skill.name} capture pose` : 'Capture pose');

          space.push({
            kind: 'singlePosition',
            id: capturePoseSpaceItemId(step.id),
            global: false,
            name,
            description: '',
            positions: [
              {
                pose,
                // A captured robot pose persists its real joint solution; fall
                // back to the all-zero sentinel only for older skills that
                // stored just the Cartesian pose. `six(0)` (not NaN, as
                // `spaceItemInlineConversion.ts` uses) so it matches the
                // runtime's `NULL_JOINT_ANGLES = six(0)`; both are rejected by
                // `hasValidJointAngles`.
                jointAngles: p.jointAngles ?? six(0),
                tcpOption: 'ee-any',
              },
            ],
          });
        } catch {
          // Defensive: a malformed capture pose should not throw the whole
          // conversion.
        }
      }
    }
  }

  return space;
}

/**
 * A space item is "virtual" when it is derived by {@link getVirtualSpaceItems}
 * rather than authored by the user: vision position lists and skill capture
 * poses. These are read-only — they're regenerated on every routine conversion
 * — so editors should present them non-editable.
 */
export function isVirtualSpaceItem(
  item: Pick<Space.Item, 'kind' | 'id'>,
): boolean {
  return (
    item.kind === 'visionPositionList' ||
    item.id.startsWith(VIRTUAL_CAPTURE_POSE_ID_PREFIX)
  );
}

export function removeVirtualSpaceItems(
  space: Space.Item[] | undefined,
): Space.Item[] | undefined {
  if (!space) {
    return undefined;
  }

  return space.filter((s) => !isVirtualSpaceItem(s));
}
